import React, { useState, useEffect } from "react";
import { Menu, X } from "lucide-react";
import { Link } from "@/components/common/Link";
import { MenuProvider, useMenu } from "@/integrations/wordpress/WordPressMenuProvider";
import { NavigationMenu, NavigationMenuItem, NavigationMenuLink, NavigationMenuList } from "@/components/ui/navigation-menu";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";
function useSmoothScroll() {
  useEffect(() => {
    const handleAnchorClick = (e: MouseEvent) => {
      const target = e.target as HTMLElement;
      const anchor = target.closest("a[href]") as HTMLAnchorElement | null;
      if (anchor) {
        const href = anchor.getAttribute("href");
        if (!href) return;
        let hash = "";
        if (href.startsWith("#")) {
          hash = href;
        } else if (href.startsWith("/") && href.includes("#")) {
          const [path, hashPart] = href.split("#");
          if (path === "/" || path === window.location.pathname) {
            hash = "#" + hashPart;
          }
        }
        if (hash && hash !== "#") {
          const element = document.querySelector(hash);
          if (element) {
            e.preventDefault();
            element.scrollIntoView({
              behavior: "smooth",
              block: "start"
            });
          }
        }
      }
    };
    document.addEventListener("click", handleAnchorClick);
    return () => document.removeEventListener("click", handleAnchorClick);
  }, []);
}
function HeaderContent({
  isScrolled
}: {
  isScrolled: boolean;
}) {
  const {
    menuItems,
    loading
  } = useMenu();
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const midPoint = Math.ceil((menuItems?.length || 0) / 2);
  const leftItems = menuItems?.slice(0, midPoint) || [];
  const rightItems = menuItems?.slice(midPoint) || [];
  const toggleMobileMenu = () => setIsMobileMenuOpen(!isMobileMenuOpen);
  const navLinkClass = cn("group relative text-xl font-sans font-medium text-foreground/80 hover:text-chart-1 transition-colors bg-transparent hover:bg-transparent focus:bg-transparent data-[active=true]:bg-transparent px-3 py-2", "after:content-[''] after:absolute after:bottom-1 after:left-1/2 after:-translate-x-1/2 after:w-0 after:h-[2px] after:bg-chart-1 hover:after:w-[calc(100%-1.5rem)] after:transition-all after:duration-300");
  return <>
      <div data-section-id="15270" className="container mx-auto px-4 h-full flex items-center justify-between relative">
        {}
        <div className="lg:hidden flex-1 flex justify-start">
          <Button variant="ghost" size="icon" onClick={toggleMobileMenu} className="text-foreground hover:bg-transparent" aria-label="Abrir menú">
            <Menu className="size-6" />
          </Button>
        </div>

        {}
        <div className="flex-shrink-0 z-10 flex justify-center items-center">
          <Link to="/HomePage" className="block hover:opacity-90 transition-opacity">
            <img src="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1577195/img/image_2k6dwy9p.png" alt="NexoMayor - Gestión Integral" className="h-16 w-auto object-contain" />
          </Link>
        </div>

        {}
        <div className="hidden lg:flex flex-1 justify-end pr-8">
          {!loading && <NavigationMenu viewport={false}>
              <NavigationMenuList className="gap-6">
                {leftItems.map((item, i) => <NavigationMenuItem key={item.id} data-index={i}>
                    <NavigationMenuLink asChild className={navLinkClass}>
                      <Link to={item.href || "#"}>{item.label}</Link>
                    </NavigationMenuLink>
                  </NavigationMenuItem>)}
              </NavigationMenuList>
            </NavigationMenu>}
        </div>

        {}
        <div className="hidden lg:flex flex-1 justify-start pl-8">
          {!loading && <NavigationMenu viewport={false}>
              <NavigationMenuList className="gap-6">
                {rightItems.map((item, i) => <NavigationMenuItem key={item.id} data-index={i + midPoint}>
                    <NavigationMenuLink asChild className={navLinkClass}>
                      <Link to={item.href || "#"}>{item.label}</Link>
                    </NavigationMenuLink>
                  </NavigationMenuItem>)}
              </NavigationMenuList>
            </NavigationMenu>}
        </div>

        {}
        <div className="lg:hidden flex-1 flex justify-end">
          {}
        </div>
      </div>

      {}
      <div className={cn("fixed inset-0 z-50 bg-background/98 backdrop-blur-md transition-all duration-500 ease-in-out lg:hidden flex flex-col", isMobileMenuOpen ? "opacity-100 pointer-events-auto translate-y-0" : "opacity-0 pointer-events-none -translate-y-4")}>
        <div className="flex justify-end p-4">
          <Button variant="ghost" size="icon" onClick={toggleMobileMenu} className="text-foreground hover:bg-transparent" aria-label="Cerrar menú">
            <X className="size-6" />
          </Button>
        </div>
        <div className="flex-1 flex flex-col items-center justify-center gap-8 p-8">
          <Link to="/HomePage" onClick={toggleMobileMenu} className="mb-4">
            <img src="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1577195/img/image_2k6dwy9p.png" alt="NexoMayor" className="h-16 w-auto" />
          </Link>
          <nav className="flex flex-col items-center gap-6">
            {menuItems.map((item, i) => <Link key={item.id} data-index={i} to={item.href || "#"} className="text-xl font-sans font-medium text-foreground hover:text-chart-1 transition-colors uppercase tracking-wide" onClick={toggleMobileMenu}>
                {item.label}
              </Link>)}
          </nav>
        </div>
      </div>
    </>;
}
export default function Encabezado() {
  const [isScrolled, setIsScrolled] = useState(false);
  useSmoothScroll();
  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 20);
    };
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);
  return <header id="header" className={cn("sticky top-0 z-50 w-full border-b transition-all duration-300 text-foreground h-20 flex items-center", isScrolled ? "bg-background/80 backdrop-blur-md border-border" : "bg-background/50 backdrop-blur-sm border-transparent")}>
      <MenuProvider menu_id="16" className="w-full h-full">
        <HeaderContent isScrolled={isScrolled} />
      </MenuProvider>
    </header>;
}